import { startTransition, useEffect, useRef } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShellLayout } from "./components/app-shell-layout";
import { AuthScreen } from "./components/auth-screen";
import { CalendarPage } from "./domains/calendar/calendar-page";
import { VaultDetailPage } from "./domains/vault/vault-page";
import { VaultsPage } from "./domains/vaults/vaults-page";
import { getActiveVaultFromStore, getSnapshotFromStore, useTodoStore } from "./store";
import { SyncClient } from "./sync";

function DefaultRouteRedirect() {
  const activeVaultId = useTodoStore((state) => state.activeVaultId);

  return <Navigate replace to={activeVaultId == null ? "/vaults" : `/vaults/${activeVaultId}`} />;
}

export function App() {
  const authStatus = useTodoStore((state) => state.authStatus);
  const hydratePersistedState = useTodoStore((state) => state.hydratePersistedState);
  const activeVault = useTodoStore((state) =>
    state.activeVaultId == null ? null : (state.vaults[state.activeVaultId] ?? null),
  );
  const activeVaultId = activeVault?.id ?? null;
  const activeVaultPassword = activeVault?.password ?? null;
  const activeVaultLastMutatedAt = activeVault?.lastMutatedAt ?? 0;
  const lastMutationSource = useTodoStore((state) => state.lastMutationSource);
  const syncClientRef = useRef<SyncClient | null>(null);
  const syncGenerationRef = useRef(0);

  useEffect(() => {
    if (authStatus !== "loading") {
      return;
    }

    void hydratePersistedState();
  }, [authStatus, hydratePersistedState]);

  useEffect(() => {
    if (authStatus !== "unlocked") {
      return;
    }

    let cancelled = false;
    let currentClient: SyncClient | null = null;
    const generation = syncGenerationRef.current + 1;
    syncGenerationRef.current = generation;
    const isCurrentGeneration = () => !cancelled && syncGenerationRef.current === generation;

    void SyncClient.create({
      getDeviceId: () => useTodoStore.getState().deviceId,
      getSnapshot: () => getSnapshotFromStore(),
      onRemoteSnapshot: (vaultId, snapshot) => {
        if (!isCurrentGeneration()) {
          return;
        }

        startTransition(() => {
          useTodoStore.getState().mergeRemoteSnapshot(vaultId, snapshot);
        });
      },
      onPeerCountChange: (count) => {
        if (!isCurrentGeneration()) {
          return;
        }

        useTodoStore.getState().setSyncState({ peerCount: count });
      },
      onStatusChange: (status, error) => {
        if (!isCurrentGeneration()) {
          return;
        }

        useTodoStore.getState().setSyncState({
          status,
          lastError: error ?? (status === "error" ? "Vault sync could not connect." : null),
        });
      },
      onSyncComplete: () => {
        if (!isCurrentGeneration()) {
          return;
        }

        useTodoStore.getState().setSyncState({ lastSyncedAt: Date.now(), lastError: null });
      },
    })
      .then((client) => {
        if (cancelled) {
          void client.stop();
          return;
        }

        currentClient = client;
        syncClientRef.current = client;
        const vault = getActiveVaultFromStore();
        void client.setVault(vault == null ? null : { id: vault.id, password: vault.password });
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : "Unknown sync error";
        useTodoStore.getState().setSyncState({
          status: "error",
          lastError: message,
        });
      });

    return () => {
      cancelled = true;

      if (syncClientRef.current === currentClient) {
        syncClientRef.current = null;
      }

      if (currentClient != null) {
        void currentClient.stop();
      }
    };
  }, [authStatus]);

  useEffect(() => {
    const client = syncClientRef.current;

    if (client == null || authStatus !== "unlocked") {
      return;
    }

    void client.setVault(
      activeVaultId == null || activeVaultPassword == null
        ? null
        : {
            id: activeVaultId,
            password: activeVaultPassword,
          },
    );
  }, [activeVaultId, activeVaultPassword, authStatus]);

  useEffect(() => {
    if (
      authStatus !== "unlocked" ||
      activeVaultLastMutatedAt === 0 ||
      lastMutationSource !== "local"
    ) {
      return;
    }

    const client = syncClientRef.current;

    if (client == null) {
      return;
    }

    void client.broadcastSnapshot();
  }, [activeVaultLastMutatedAt, authStatus, lastMutationSource]);

  if (authStatus !== "unlocked") {
    return <AuthScreen />;
  }

  return (
    <Routes>
      <Route element={<AppShellLayout />}>
        <Route element={<DefaultRouteRedirect />} index />
        <Route element={<CalendarPage />} path="calendar" />
        <Route element={<VaultsPage />} path="vaults" />
        <Route element={<VaultDetailPage />} path="vaults/:vaultId" />
        <Route element={<DefaultRouteRedirect />} path="*" />
      </Route>
    </Routes>
  );
}
